import { useState, type KeyboardEvent } from 'react';

import { RatingStar } from './RatingStar';

export interface RatingProps {
  /** 1 to 5 in half steps, or null when unrated. */
  value: number | null;
  /** Makes the rating an input. Leave out for a read-only display. */
  onChange?: (value: number) => void;
  size?: number;
  /** Accessible name for the input form. */
  label?: string;
  /** Id of an element that names the input. Wins over `label`. */
  labelledBy?: string;
}

const MIN = 1;
const MAX = 5;
const STEP = 0.5;
const STARS = [1, 2, 3, 4, 5];

const describeScore = (value: number | null) =>
  value === null ? 'Not rated' : `${value} out of 5`;

const clamp = (n: number) => Math.min(MAX, Math.max(MIN, n));

/** Where a key press sends the rating, or null when the key is not ours. */
function nextValue(key: string, value: number | null): number | null {
  const from = value ?? MIN - STEP;
  switch (key) {
    case 'ArrowRight':
    case 'ArrowUp':
      return clamp(from + STEP);
    case 'ArrowLeft':
    case 'ArrowDown':
      return clamp(from - STEP);
    case 'Home':
      return MIN;
    case 'End':
      return MAX;
    default:
      return null;
  }
}

/** Half-step star rating. A display on its own, a slider when given `onChange`. */
export function Rating({ value, onChange, size = 18, label = 'Rating', labelledBy }: RatingProps) {
  const [hover, setHover] = useState<number | null>(null);
  const shown = hover ?? value;

  const stars = STARS.map((i) => (
    <RatingStar
      key={i}
      index={i}
      shown={shown}
      size={size}
      interactive={onChange !== undefined}
      onHover={setHover}
      onPick={onChange}
    />
  ));

  if (!onChange) {
    return (
      <span
        role="img"
        aria-label={describeScore(value)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
      >
        {stars}
      </span>
    );
  }

  const onKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
    const next = nextValue(e.key, value);
    if (next === null) return;
    e.preventDefault();
    onChange(next);
  };

  return (
    <span
      role="slider"
      tabIndex={0}
      className="nb-rating"
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      aria-valuemin={MIN}
      aria-valuemax={MAX}
      aria-valuenow={value ?? undefined}
      aria-valuetext={describeScore(value)}
      onKeyDown={onKeyDown}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
    >
      {stars}
    </span>
  );
}
